import { useState } from 'react';
import {
    IconChevronLeft,
    IconChevronRight,
    IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import Hint from '../../../components/common/Hint';
import { PanelCloseButton } from '../ui/prims';

/**
 * The outline, down the side of the editor.
 *
 * It is the same tree the exported PDF carries and the same one a reader shows
 * in its own panel, so what is edited here is what the file will say. Clicking
 * an entry goes to its page; the arrows move it in or out a level, which is the
 * only structure a PDF outline has.
 */
export default function BookmarksPanel() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const bookmarks = usePdfEditorStore((s) => s.bookmarks);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const updateBookmark = usePdfEditorStore((s) => s.updateBookmark);
    const removeBookmark = usePdfEditorStore((s) => s.removeBookmark);
    const toggleBookmarks = usePdfEditorStore((s) => s.toggleBookmarks);

    const [editingId, setEditingId] = useState(null);
    const digits = (n) => (isBn ? toBnDigits(n) : String(n));

    return (
        <aside className="flex h-full w-[232px] flex-none flex-col border-r border-[#ECE8E4] bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex h-10 flex-none items-center justify-between px-3 text-[12px] font-bold text-[#3D3833] dark:text-zinc-200">
                {t('pdfEditor.bookmarks.title')}
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-[#A8A099]">
                        {digits(bookmarks.length)}
                    </span>
                    <PanelCloseButton onClick={toggleBookmarks} />
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 [scrollbar-width:thin]">
                {bookmarks.length === 0 ? (
                    <p className="px-2 py-6 text-center text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.bookmarks.empty')}
                    </p>
                ) : bookmarks.map((mark) => (
                    <div
                        key={mark.id}
                        className={`group mb-1 flex items-center gap-1 rounded-lg px-1.5 py-1.5 transition-colors ${
                            mark.pageIndex === currentPage
                                ? 'bg-primary-50 dark:bg-primary-950/30'
                                : 'hover:bg-[#F7F5F3] dark:hover:bg-zinc-800'
                        }`}
                        style={{ paddingLeft: 6 + (mark.level || 0) * 14 }}
                    >
                        {editingId === mark.id ? (
                            <input
                                autoFocus
                                defaultValue={mark.title}
                                onBlur={(e) => {
                                    const title = e.target.value.trim();
                                    if (title) updateBookmark(mark.id, { title });
                                    setEditingId(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                    if (e.key === 'Escape') setEditingId(null);
                                }}
                                className="h-6 min-w-0 flex-1 rounded border border-primary bg-white px-1.5 text-[12px] text-[#1C1815] outline-none dark:bg-zinc-900 dark:text-zinc-100"
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => goToPage(mark.pageIndex)}
                                onDoubleClick={() => setEditingId(mark.id)}
                                title={t('pdfEditor.bookmarks.rename')}
                                className="min-w-0 flex-1 truncate text-left text-[12px] text-[#3D3833] dark:text-zinc-200"
                            >
                                {mark.title}
                            </button>
                        )}

                        <span className="flex-none text-[10.5px] tabular-nums text-[#A8A099]">
                            {digits(mark.pageIndex + 1)}
                        </span>

                        {/* The level controls only appear on the row under the
                            pointer: three buttons on every line would read as
                            more structure than a PDF outline actually has. */}
                        <div className="flex flex-none items-center opacity-0 transition-opacity group-hover:opacity-100">
                            <Hint label={t('pdfEditor.bookmarks.promote')}>
                                <button
                                    type="button"
                                    disabled={!mark.level}
                                    onClick={() => updateBookmark(mark.id, { level: 0 })}
                                    className="flex h-5 w-5 items-center justify-center rounded text-[#6B635C] hover:bg-[#ECE8E4] disabled:opacity-30 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                >
                                    <IconChevronLeft size={13} stroke={2} />
                                </button>
                            </Hint>
                            <Hint label={t('pdfEditor.bookmarks.demote')}>
                                <button
                                    type="button"
                                    disabled={!!mark.level}
                                    onClick={() => updateBookmark(mark.id, { level: 1 })}
                                    className="flex h-5 w-5 items-center justify-center rounded text-[#6B635C] hover:bg-[#ECE8E4] disabled:opacity-30 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                >
                                    <IconChevronRight size={13} stroke={2} />
                                </button>
                            </Hint>
                            <Hint label={t('common.delete')}>
                                <button
                                    type="button"
                                    onClick={() => removeBookmark(mark.id)}
                                    className="flex h-5 w-5 items-center justify-center rounded text-[#6B635C] hover:bg-[#FDECEA] hover:text-[#D33A2C] dark:text-zinc-400"
                                >
                                    <IconTrash size={13} stroke={1.9} />
                                </button>
                            </Hint>
                        </div>
                    </div>
                ))}
            </div>
        </aside>
    );
}
